
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  Settings, 
  DollarSign,
  BarChart3,
  FileCheck,
  ChevronDown, 
  ChevronRight, 
  Plus
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface SidebarProps {
  isOpen: boolean;
}

interface SidebarLinkProps {
  to: string;
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  onClick?: () => void;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, icon: Icon, label, isActive, onClick }) => {
  // If there's a click handler, render a button
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={cn(
          "flex items-center gap-3 px-3 py-2 rounded-md transition-colors w-full text-left text-sm min-h-[44px]",
          isActive 
            ? "bg-primary-foreground/10 text-primary-foreground" 
            : "text-primary-foreground/80 hover:bg-primary/80 hover:text-primary-foreground"
        )}
      >
        <Icon size={18} />
        <span>{label}</span>
      </button>
    );
  }

  // Otherwise render a Link for client-side navigation
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm min-h-[44px]",
        isActive 
          ? "bg-primary-foreground/10 text-primary-foreground" 
          : "text-primary-foreground/80 hover:bg-primary/80 hover:text-primary-foreground"
      )}
    >
      <Icon size={18} />
      <span>{label}</span>
    </Link>
  );
};

const VendorPressSidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Check if the path is active
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Define state for collapsible sections
  const [openSections, setOpenSections] = useState<string[]>(["Press"]);

  const toggleSection = (section: string) => {
    setOpenSections(prev => 
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Function to handle create new article
  const handleCreateArticle = () => {
    navigate("/vendor-press-dashboard/articles/edit/new");
  };

  // Top level navigation items that are always visible
  const topLevelItems = [
    { path: "/vendor-press-dashboard", label: "Dashboard", icon: LayoutDashboard }
  ];

  // Collapsed sidebar view
  if (!isOpen) {
    return (
      <div className="w-16 bg-primary flex-shrink-0 flex flex-col h-full overflow-y-auto transition-all duration-300 hidden lg:flex">
        <div className="flex flex-col items-center py-4">
          {/* Top level links for collapsed sidebar */}
          {topLevelItems.map((item) => {
            const ItemIcon = item.icon;
            const isItemActive = location.pathname === item.path;
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={cn(
                  "p-3 mb-2 rounded-lg transition-colors min-h-[44px] min-w-[44px]",
                  isItemActive 
                    ? "bg-primary/80 text-primary-foreground" 
                    : "text-primary-foreground hover:bg-primary/80"
                )}
              >
                <ItemIcon size={20} />
              </button>
            );
          })}
          
          {/* Separator after top level links */}
          <div className="my-2 w-6 h-px bg-primary-foreground/20"></div>
          
          {/* Quick action button for new article */}
          <button
            onClick={handleCreateArticle}
            className="p-3 mb-2 rounded-lg transition-colors text-primary-foreground hover:bg-primary/80 min-h-[44px] min-w-[44px]"
            title="Create New Article"
          >
            <Plus size={20} />
          </button>
          
          {/* Press section for collapsed sidebar */}
          <button
            className="p-3 mb-2 rounded-lg transition-colors text-primary-foreground hover:bg-primary/80 min-h-[44px] min-w-[44px]"
            onClick={() => navigate("/vendor-press-dashboard/articles")}
          >
            <FileText size={20} />
          </button>

          {/* Finance for collapsed sidebar */}
          <button
            className="p-3 mb-2 rounded-lg transition-colors text-primary-foreground hover:bg-primary/80 min-h-[44px] min-w-[44px]"
            onClick={() => navigate("/vendor-press-dashboard/finance")}
          >
            <DollarSign size={20} />
          </button>

          {/* Analytics for collapsed sidebar */}
          <button
            className="p-3 mb-2 rounded-lg transition-colors text-primary-foreground hover:bg-primary/80 min-h-[44px] min-w-[44px]"
            onClick={() => navigate("/vendor-press-dashboard/analytics")}
          >
            <BarChart3 size={20} />
          </button>
          
          <div className="my-2 w-6 h-px bg-primary-foreground/20"></div>
          
          {/* W-9 for collapsed sidebar */}
          <button
            className="p-3 mb-2 rounded-lg transition-colors text-primary-foreground hover:bg-primary/80 min-h-[44px] min-w-[44px]"
            onClick={() => navigate("/vendor-press-dashboard/w9")}
            title="W-9 Form"
          >
            <FileCheck size={20} />
          </button>
        </div>
      </div>
    );
  }

  // Expanded sidebar view
  return (
    <div className={cn(
      "bg-primary flex-shrink-0 flex flex-col h-full overflow-y-auto transition-all duration-300",
      isMobile 
        ? "fixed inset-y-0 left-0 z-50 w-64 transform" + (isOpen ? " translate-x-0" : " -translate-x-full")
        : "w-64"
    )}>
      <div className="p-4 flex flex-col h-full">
        {/* Top Level Links - Always Visible */}
        <div className="mb-4 space-y-1.5">
          {topLevelItems.map((item) => (
            <SidebarLink
              key={item.path}
              to={item.path}
              icon={item.icon}
              label={item.label}
              isActive={location.pathname === item.path}
            />
          ))}
        </div>

        {/* Quick Create Button */}
        <div className="mb-4">
          <button
            onClick={handleCreateArticle}
            className="w-full flex items-center gap-3 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm font-medium min-h-[44px]"
          >
            <Plus size={18} />
            <span>Create New Article</span>
          </button>
        </div>

        {/* Separator between top level links and collapsible sections */}
        <Separator className="my-3 bg-primary-foreground/20" />
        
        {/* Press Section */}
        <div className="mb-2">
          <Collapsible 
            open={openSections.includes("Press")}
            onOpenChange={() => toggleSection("Press")}
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-primary-foreground/80 hover:bg-primary/80 hover:text-primary-foreground rounded-md">
              <span>PRESS MANAGEMENT</span>
              {openSections.includes("Press") ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </CollapsibleTrigger>
            
            <CollapsibleContent className="mt-1.5">
              <div className="space-y-1.5 pl-1">
                <SidebarLink
                  to="/vendor-press-dashboard/articles"
                  icon={FileText}
                  label="My Articles"
                  isActive={isActive("/vendor-press-dashboard/articles")}
                />
                <SidebarLink
                  to="/vendor-press-dashboard/mentions"
                  icon={MessageSquare}
                  label="Mentions"
                  isActive={isActive("/vendor-press-dashboard/mentions")}
                />
                <SidebarLink
                  to="/vendor-press-dashboard/finance"
                  icon={DollarSign}
                  label="Finance"
                  isActive={isActive("/vendor-press-dashboard/finance")}
                />
                <SidebarLink
                  to="/vendor-press-dashboard/analytics"
                  icon={BarChart3}
                  label="Analytics"
                  isActive={isActive("/vendor-press-dashboard/analytics")}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Account Section */}
        <div className="mt-auto pt-4 border-t border-primary-foreground/20 space-y-1.5">
          <SidebarLink
            to="/vendor-press-dashboard/w9"
            icon={FileCheck}
            label="W-9 Form"
            isActive={isActive("/vendor-press-dashboard/w9")}
          />
          <SidebarLink
            to="/vendor-press-dashboard/settings"
            icon={Settings}
            label="Settings"
            isActive={isActive("/vendor-press-dashboard/settings")}
          />
        </div>
      </div>
    </div>
  );
};

export default VendorPressSidebar;
